import { useState } from "react";
import api from "../../services/api";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import "./SalesReport.css";

export default function SalesReport() {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleGenerateReport = async () => {
    if (!fromDate || !toDate) {
      return Swal.fire("Notice", "You must select both dates", "warning");
    }

    if (new Date(fromDate) > new Date(toDate)) {
      return Swal.fire("Error", "The start date cannot be later than the end date", "error");
    }

    setLoading(true);
    try {
      const response = await api.get("/reports/sales", {
        params: { from: fromDate, to: toDate },
      });

      setReportData(response.data);

      Swal.fire({
        icon: "success",
        title: "Report Generated",
        text: "The report was generated successfully",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error generating report:", error);
      Swal.fire("Error", "Could not generate the report", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFromDate("");
    setToDate("");
    setReportData(null);
  };

  const handleExportExcel = () => {
    if (!reportData) return;

    const workbook = XLSX.utils.book_new();

    const summaryData = [
      ["SALES REPORT"],
      ["Period:", `${reportData.periodo.desde} to ${reportData.periodo.hasta}`],
      [],
      ["GENERAL SUMMARY"],
      ["Total Sales", `$${reportData.resumen.total_ventas.toFixed(2)}`],
      ["Total Transactions", reportData.resumen.total_facturas],
      ["Total Sales Tax", `$${reportData.resumen.total_sales_tax.toFixed(2)}`],
      ["Subtotal", `$${reportData.resumen.total_subtotal.toFixed(2)}`],
      ["Average Sale", `$${reportData.resumen.promedio_venta.toFixed(2)}`],
    ];
    const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, wsSummary, "Summary");

    const salesByDay = reportData.ventas_por_dia.map((day) => ({
      Date: day.fecha,
      "Total Transactions": day.total_facturas,
      Subtotal: day.subtotal,
      "Sales Tax": day.sales_tax,
      Total: day.total,
    }));
    const wsSales = XLSX.utils.json_to_sheet(salesByDay);
    XLSX.utils.book_append_sheet(workbook, wsSales, "Sales by Day");

    const topProducts = reportData.top_productos.map((prod) => ({
      Code: prod.codigo,
      Product: prod.nombre,
      "Qty Sold": prod.cantidad_vendida,
      "Total Sold": prod.total_vendido,
    }));
    const wsProducts = XLSX.utils.json_to_sheet(topProducts);
    XLSX.utils.book_append_sheet(workbook, wsProducts, "Top Products");

    const fileName = `Sales_Report_${fromDate}_to_${toDate}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    Swal.fire({
      icon: "success",
      title: "Excel Downloaded",
      timer: 2000,
      showConfirmButton: false,
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);
  };

  return (
    <div className="sales-report-container">
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">📊 Sales Report</h2>
        </div>

        <div className="card-body">
          <div className="filters-section">
            <div className="filters-row">
              <div className="form-group">
                <label className="form-label">📅 Start Date</label>
                <input type="date" className="form-control" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
              </div>

              <div className="form-group">
                <label className="form-label">📅 End Date</label>
                <input type="date" className="form-control" value={toDate} onChange={(e) => setToDate(e.target.value)} />
              </div>

              <div className="filters-actions">
                <button className="btn btn-primary" onClick={handleGenerateReport} disabled={loading}>
                  {loading ? "⏳ Generating..." : "🔍 Generate Report"}
                </button>
                <button className="btn btn-secondary" onClick={handleClear}>
                  🔄 Clear
                </button>
              </div>
            </div>
          </div>

          {loading && (
            <div className="text-center mt-4">
              <div className="spinner"></div>
              <p className="mt-2">Generating report...</p>
            </div>
          )}

          {reportData && !loading && (
            <div className="report-results">
              <div className="export-section">
                <button className="btn btn-success" onClick={handleExportExcel}>
                  📊 Export to Excel
                </button>
              </div>

              <div className="summary-section">
                <h3 className="section-title">📈 General Summary</h3>
                <div className="kpi-cards">
                  <div className="kpi-card">
                    <div className="kpi-icon">💰</div>
                    <div className="kpi-content">
                      <div className="kpi-label">Total Sales</div>
                      <div className="kpi-value">{formatCurrency(reportData.resumen.total_ventas)}</div>
                    </div>
                  </div>

                  <div className="kpi-card">
                    <div className="kpi-icon">📄</div>
                    <div className="kpi-content">
                      <div className="kpi-label">Total Transactions</div>
                      <div className="kpi-value">{reportData.resumen.total_facturas}</div>
                    </div>
                  </div>

                  <div className="kpi-card">
                    <div className="kpi-icon">💵</div>
                    <div className="kpi-content">
                      <div className="kpi-label">Total Sales Tax</div>
                      <div className="kpi-value">{formatCurrency(reportData.resumen.total_sales_tax)}</div>
                    </div>
                  </div>

                  <div className="kpi-card">
                    <div className="kpi-icon">📊</div>
                    <div className="kpi-content">
                      <div className="kpi-label">Average Sale</div>
                      <div className="kpi-value">{formatCurrency(reportData.resumen.promedio_venta)}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="sales-by-day-section">
                <h3 className="section-title">📅 Sales by Day</h3>
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Transactions</th>
                        <th>Subtotal</th>
                        <th>Sales Tax</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.ventas_por_dia.map((day, index) => (
                        <tr key={index}>
                          <td>{new Date(day.fecha).toLocaleDateString("en-US")}</td>
                          <td className="text-center">{day.total_facturas}</td>
                          <td className="text-right">{formatCurrency(day.subtotal)}</td>
                          <td className="text-right">{formatCurrency(day.sales_tax)}</td>
                          <td className="text-right font-bold">{formatCurrency(day.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="total-row">
                        <td><strong>TOTAL</strong></td>
                        <td className="text-center"><strong>{reportData.resumen.total_facturas}</strong></td>
                        <td className="text-right"><strong>{formatCurrency(reportData.resumen.total_subtotal)}</strong></td>
                        <td className="text-right"><strong>{formatCurrency(reportData.resumen.total_sales_tax)}</strong></td>
                        <td className="text-right"><strong>{formatCurrency(reportData.resumen.total_ventas)}</strong></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              <div className="top-products-section">
                <h3 className="section-title">🏆 Top 10 Best-Selling Products</h3>

                <div className="chart-container">
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={reportData.top_productos} layout="vertical" margin={{ top: 5, right: 30, left: 100, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="nombre" type="category" width={150} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="cantidad_vendida" fill="#8884d8" name="Qty Sold" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="table-container mt-3">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Code</th>
                        <th>Product</th>
                        <th>Qty Sold</th>
                        <th>Total Sold</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.top_productos.map((product, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>{product.codigo}</td>
                          <td>{product.nombre}</td>
                          <td className="text-center">{product.cantidad_vendida}</td>
                          <td className="text-right">{formatCurrency(product.total_vendido)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {!reportData && !loading && (
            <div className="no-data">
              <p>📭 Select a date range and generate the report</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}